import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import {AzPageHeader} from '../../../components/AzPageHeader'
import { isNotEmpty, KTCard, KTCardBody, ID, QUERIES } from '../../../../_dashboard/helpers'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { useQuery, useQueryClient } from 'react-query'
import { getOneByIdbylang, getAll, patchOne, createOne, deleteOne } from '../../../services/common_requests'
import { useListView } from '../tours-list/core/ListViewProvider'
import { useQueryResponse } from '../tours-list/core/QueryResponseProvider'
import { useIntl, IntlShape } from 'react-intl'
import {toast} from 'react-toastify'
import { TourDetails, initialTour, images } from './core/_models'
import {
  buildTourTranslationPayload,
  findTranslationByLanguage,
  hasTourTranslationChanged,
  normalizeIdList,
} from './core/translationSubmit'
import { sanitizeTourPatchPayload, TOUR_PATCH_IGNORE_FIELDS } from './core/tourPatchPayload'
import { categoryIdsForSubmit, extractCategoryIds } from './core/categoryHelpers'
import { GeneralTab } from './tabs/GeneralTab'
import { FormikProps } from 'formik'
import { LocationTab } from './tabs/LocationTab'
import { PricingTab } from './tabs/PricingTab/index'
import { AvailabilityTab } from './tabs/AvailabilityTab'
import { FaqIncludesExcludesTab } from './tabs/FaqIncludesExcludesTab'
import { Tooltip, OverlayTrigger } from 'react-bootstrap'
import StickyLanguageSelector from '../../../components/StickyLanguageSelector'
import { SeoTab } from './tabs/SeoTab'
import {useAuth} from '../../../modules/auth'
import {isSuperAdmin} from '../../../utils/roleUtils'
import {
  computeBookingDeadlineDays,
  computeLastBookingDateFromDays,
  isBookingDeadlineDaysProvided,
} from './utils/bookingDeadlineDays'
import {
  isContentCategoryType,
  localizeDetails,
} from './tabs/contentCategoryUtils'

/** Safe ObjectId extraction: typeof null === 'object' in JS */
const toRefId = (value: unknown): string => {
  if (value == null || value === '') return ''
  if (typeof value === 'object' && value !== null && '_id' in (value as object)) {
    const id = (value as {_id?: unknown})._id
    return id != null ? String(id) : ''
  }
  if (typeof value === 'object' && value !== null && 'id' in (value as object)) {
    const id = (value as {id?: unknown}).id
    return id != null ? String(id) : ''
  }
  return String(value)
}

// Add custom CSS for pill-style tabs that exactly match the image
const customTabStyles = `
  .tour-tabs-container {
    margin-bottom: 2rem;
  }
  
  .tour-title {
    font-size: 1.75rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
  }
  
  .tour-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 2rem;
  }
  
  .tour-tab-item {
    border: 1px solid #d0d7de;
    background-color: #ffffff;
    color: #334155;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 0.6rem 1.4rem;
    border-radius: 2rem;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
  }
  
  .tour-tab-item:hover {
    background-color: #e8f1fb;
    color: #00347f;
    border-color: #93c5fd;
  }
  
  .tour-tab-item.active {
    background-color: #00347f;
    color: #ffffff;
    border-color: #00347f;
    box-shadow: 0 2px 8px rgba(0, 52, 127, 0.25);
  }
  
  .tour-tab-item.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: #e9ecef;
    color: #6c757d;
    pointer-events: none;
  }
  
  .tour-tab-item.disabled:hover {
    background-color: #e9ecef;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.03);
  }
  
  /* Validation error styles */
  .form-control.is-invalid,
  .form-select.is-invalid {
    border-color: #f1416c !important;
    padding-right: calc(1.5em + 1.55rem) !important;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23f1416c'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23f1416c' stroke='none'/%3e%3c/svg%3e") !important;
    background-repeat: no-repeat !important;
    background-position: right calc(0.375em + 0.3875rem) center !important;
    background-size: calc(0.75em + 0.775rem) calc(0.75em + 0.775rem) !important;
  }
  
  .invalid-feedback {
    display: block;
    width: 100%;
    margin-top: 0.5rem;
    font-size: 0.925rem;
    color: #f1416c;
  }
  
  /* Shake animation for invalid fields */
  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
    20%, 40%, 60%, 80% { transform: translateX(5px); }
  }
  
  .shake {
    animation: shake 0.6s cubic-bezier(.36,.07,.19,.97) both;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = customTabStyles;
  document.head.appendChild(styleElement);
}

// Temporary placeholder components with proper typing
interface TabProps {
  formik: FormikProps<TourDetails>
  intl: IntlShape
  selectedLanguage: string
}

export interface DeleteResponse {
  payload?: {
    code?: number
  }
}

// Helper to format date based on language
function formatDate(dateString: string, lang: string) {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  if (lang === 'fr') {
    // DD/MM/YYYY
    return date.toLocaleDateString('fr-FR');
  } else {
    // MM/DD/YYYY
    return date.toLocaleDateString('en-US');
  }
}

type BlackoutRangeRow = {
  from?: string
  to?: string
  type?: string
  message?: string
}

type BlackoutRangeWithDates = { from: string; to: string }

function isBlackoutRangeWithDates(r: unknown): r is BlackoutRangeWithDates {
  if (!r || typeof r !== 'object') return false
  const o = r as Record<string, unknown>
  return typeof o.from === 'string' && typeof o.to === 'string' && o.from !== '' && o.to !== ''
}

function buildBookingBlackoutRangesSchema(intl: IntlShape) {
  return Yup.array()
    .of(
      Yup.object({
        from: Yup.string().required(),
        to: Yup.string().required(),
        type: Yup.string().oneOf(['blocked', 'custom_request']).optional(),
        message: Yup.string().optional(),
      }).test(
        'from-before-to',
        intl.formatMessage({ id: 'TOUR.AVAILABILITY.VALIDATION_END_AFTER_START' }),
        (row) => {
          if (!row?.from || !row?.to) return true
          return new Date(row.from) <= new Date(row.to)
        },
      ),
    )
    .test(
      'no-overlap',
      intl.formatMessage({ id: 'TOUR.AVAILABILITY.VALIDATION_BLACKOUTS_NO_OVERLAP' }),
      (ranges) => {
        const rows = (ranges || []).filter(isBlackoutRangeWithDates) as BlackoutRangeWithDates[]
        if (rows.length < 2) return true
        const sorted = [...rows].sort(
          (a, b) => new Date(a.from).getTime() - new Date(b.from).getTime(),
        )
        for (let i = 1; i < sorted.length; i++) {
          if (new Date(sorted[i].from) <= new Date(sorted[i - 1].to)) {
            return false
          }
        }
        return true
      },
    )
}

const TourDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const [selectedLanguage, setSelectedLanguage] = useState('fr')
  const [activeTab, setActiveTab] = useState('general')
  const activeTabRef = useRef(activeTab)
  useEffect(() => {
    activeTabRef.current = activeTab
  }, [activeTab])
  const { refetchData } = useQueryResponse()
  const intl = useIntl()
  const queryClient = useQueryClient()
  const isNewTour = !id || id === 'new'

  // Set active tab when location state changes
  useEffect(() => {
    const state = location.state as { initialTab?: string } | null
    if (state && state.initialTab) {
      setActiveTab(state.initialTab)
    }
  }, [location.state])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [deleteSuccessful, setDeleteSuccessful] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const {auth} = useAuth()
  const canDeleteTour = isSuperAdmin(auth?.role || localStorage.getItem('role'))

  // Add fetchTourDetails function
  const fetchTourDetails = async () => {
    if (!id) return
    try {
      const response = await getOneByIdbylang(`/tours/${id}?language=${selectedLanguage}`)
      if (response) {
        // Prefer dedicated by-tour multi-lang payload; never keep wiped formik cats
        const cats = (
          (contentCategoriesAll && contentCategoriesAll.length
            ? contentCategoriesAll
            : response.contentCategories) || []
        ) as any[]
        const includesSource = cats.find((item) => isContentCategoryType(item?.type, 'includes'))
        const excludesSource = cats.find((item) => isContentCategoryType(item?.type, 'excludes'))
        formik.setValues({
          ...response,
          city: toRefId(response.city),
          contentCategories: cats,
          includes: localizeDetails(includesSource?.details, selectedLanguage),
          excludes: localizeDetails(excludesSource?.details, selectedLanguage),
        })
      }
    } catch (error) {
      console.error('Error fetching tour details:', error)
      toast.error('Failed to fetch tour details')
    }
  }

  // Add debug log function
  const addDebugLog = (message: string) => {
    // Debug logs removed
  }

  // Define tabs with their properties
  const tabs = [
    { 
      id: 'general', 
      name: intl.formatMessage({ id: 'TOUR.FIELDS.GENERAL' }),
      requiresTourId: false 
    },
    { 
      id: 'location', 
      name: intl.formatMessage({ id: 'TOUR.FIELDS.LOCATION' }),
      requiresTourId: true 
    },
    { 
      id: 'pricing', 
      name: intl.formatMessage({ id: 'TOUR.FIELDS.PRICING' }),
      requiresTourId: true 
    },
    {
      id: 'availability',
      name: intl.formatMessage({ id: 'TOUR.FIELDS.AVAILABILITY' }),
      requiresTourId: true,
    },
    { 
      id: 'faq-includes-excludes', 
      name: intl.formatMessage({ id: 'TOUR.FIELDS.FAQ_INCLUDES_EXCLUDES' }),
      requiresTourId: true 
    },
    {
      id: 'seo',
      name: intl.formatMessage({ id: 'TOUR.FIELDS.SEO' }),
      requiresTourId: true
    }
  ]

  const handleTabClick = (tabId: string) => {
    const tab = tabs.find(t => t.id === tabId)
    if (!tab?.requiresTourId || !isNewTour) {
      setActiveTab(tabId)
    }
  }

  const handleCancel = () => {
    navigate('/apps/tours')
  }

  const { itemIdForUpdate } = useListView()
  const enabledQuery: boolean = isNotEmpty(id)
  const { data: tour } = useQuery(
    `tour-${id}-${selectedLanguage}`,
    () => {
      if (!id || isNewTour) return null
      return getOneByIdbylang(`/tours/${id}?language=${selectedLanguage}`)
    },
    { 
      enabled: !!id && !isNewTour,
      staleTime: 0, // Consider data stale immediately
      cacheTime: 0, // Don't cache the data
      refetchOnMount: true, // Refetch when component mounts
      refetchOnWindowFocus: true // Refetch when window regains focus
    }
  )

  const { data: tourAllLang } = useQuery(
    `tour-all-lang-${id}`,
    () => {
      if (!id || isNewTour) return null
      return getOneByIdbylang(`/tours/${id}?language=all`)
    },
    {
      enabled: !!id && !isNewTour,
      staleTime: 0,
      cacheTime: 0,
    },
  )

  const { data: contentCategoriesAll, isFetched: contentCategoriesFetched } = useQuery(
    `tour-content-categories-${id}`,
    async () => {
      if (!id || isNewTour) return []
      const res = await getAll({}, `/tour-content-categories/by-tour/${id}?language=all`)
      return Array.isArray(res?.data) ? res.data : []
    },
    {
      enabled: !!id && !isNewTour,
      staleTime: 0,
      cacheTime: 0,
    },
  )

  const prepareDataForSubmit = (values: TourDetails) => {
    // For PATCH requests, send only modified fields
    if (!isNewTour) {
      // Get only the modified fields
      const modifiedFields = Object.keys(values).filter(key => {
        if (TOUR_PATCH_IGNORE_FIELDS.has(key)) {
          return false
        }
        if (key === 'translations') {
          const originalTranslation = findTranslationByLanguage(
            tourAllLang?.translations ?? tour?.translations,
            selectedLanguage,
          )
          return hasTourTranslationChanged(
            values.translations?.[0] as Record<string, unknown> | undefined,
            originalTranslation as Record<string, unknown> | undefined,
          )
        }
        // Compare values, handling special cases
        if (key === 'city') {
          return toRefId(values.city) !== toRefId(tour?.city)
        }
        if (key === 'routeCities') {
          const normIds = (arr: any[]) =>
            JSON.stringify(
              (arr || [])
                .map((c) => (typeof c === 'object' && c?._id ? String(c._id) : String(c)))
                .filter((id) => id && id !== 'undefined')
                .sort(),
            )
          return normIds(values.routeCities || []) !== normIds((tour as any)?.routeCities || [])
        }
        if (key === 'category') {
          return (
            JSON.stringify(extractCategoryIds(values.category).sort()) !==
            JSON.stringify(extractCategoryIds(tour?.category).sort())
          )
        }
        if (key === 'types') {
          const normTypeIds = (types: TourDetails['types']) => {
            if (Array.isArray(types)) {
              return normalizeIdList(types)
            }
            if (types && typeof types === 'object' && '_id' in types) {
              return normalizeIdList([types])
            }
            return []
          }
          return (
            JSON.stringify(normTypeIds(values.types)) !==
            JSON.stringify(normTypeIds(tour?.types))
          )
        }
        if (key === 'gallery') {
          const newGalleryIds = Array.isArray(values.gallery) 
            ? values.gallery.map((img: any) => img._id || img)
            : []
          const oldGalleryIds = Array.isArray(tour?.gallery)
            ? tour.gallery.map((img: any) => img._id || img)
            : []
          return JSON.stringify(newGalleryIds) !== JSON.stringify(oldGalleryIds)
        }
        if (key === 'banner') {
          const newBannerId = values.banner?._id || values.banner
          const oldBannerId = tour?.banner?._id || tour?.banner
          return newBannerId !== oldBannerId
        }
        if (key === 'price' || key === 'promoPrice') {
          return String(values[key as keyof TourDetails]) !== String(tour?.[key as keyof TourDetails])
        }
        if (key === 'startDate' || key === 'endDate') {
          const tourDateKey = key === 'startDate' ? 'disponibilite_from' : 'disponibilite_to'
          return String(values[key as keyof TourDetails] ?? '') !== String(tour?.[tourDateKey as keyof typeof tour] ?? '')
        }
        if (key === 'published') {
          const newStatus = values.published ? 'published' : 'draft'
          return newStatus !== (tour?.status || 'draft')
        }
        if (key === 'bookingDeadlineDays') {
          const original = computeBookingDeadlineDays(
            tour?.endDate || (tour as any)?.disponibilite_to,
            tour?.lastBookingDate,
          )
          const current = isBookingDeadlineDaysProvided(values.bookingDeadlineDays)
            ? Number(values.bookingDeadlineDays)
            : null
          return current !== original
        }
        if (key === 'lastBookingDate') {
          return false
        }
        if (key === 'bookingBlackoutRanges') {
          const normalize = (rows: any[]) =>
            JSON.stringify(
              (rows || [])
                .map((r) => ({
                  from: r?.from,
                  to: r?.to,
                  type: r?.type || 'blocked',
                  message: r?.message || '',
                }))
                .sort((a, b) => a.from.localeCompare(b.from)),
            )
          return normalize(values.bookingBlackoutRanges || []) !== normalize((tour as any)?.bookingBlackoutRanges || [])
        }
        if (key === 'routeWaypoints') {
          const normWp = (arr: any[]) =>
            JSON.stringify(
              (arr || [])
                .map((w, i) => ({
                  order: typeof w?.order === 'number' ? w.order : i + 1,
                  title: (w?.title || '').trim(),
                  lat: Number(w?.lat),
                  lng: Number(w?.lng),
                }))
                .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
                .sort((a, b) => a.order - b.order),
            )
          return normWp(values.routeWaypoints || []) !== normWp((tour as any)?.routeWaypoints || [])
        }
        if (key === 'invalidBookingDateMessage') {
          return (values.invalidBookingDateMessage || '') !== ((tour as any)?.invalidBookingDateMessage || '')
        }
        if (key === 'isBookable') {
          const nv = values.isBookable !== false
          const ov = (tour as any)?.isBookable !== false
          return nv !== ov
        }
        if (key === 'notBookableMessage') {
          return (
            JSON.stringify(values.notBookableMessage || {}) !==
            JSON.stringify((tour as any)?.notBookableMessage || {})
          )
        }
        if (key === 'videos') {
          const normVideos = (arr: any[]) =>
            JSON.stringify(
              (arr || [])
                .filter((v) => v && String(v.url || '').trim())
                .map((v) => ({
                  type: v.type || 'youtube',
                  url: String(v.url || '').trim(),
                  title: String(v.title || '').trim(),
                })),
            )
          return normVideos(values.videos || []) !== normVideos((tour as any)?.videos || [])
        }
        return values[key as keyof TourDetails] !== tour?.[key as keyof TourDetails]
      })

      // Create data object with only modified fields
      const dataToSubmit = modifiedFields.reduce((acc: any, key) => {
        // Handle special cases for data formatting
        if (key === 'city') {
          const raw = toRefId(values.city)
          acc[key] = raw === '' ? null : raw
        } else if (key === 'routeCities') {
          acc[key] = (values.routeCities || [])
            .map((c: any) => (typeof c === 'object' && c?._id ? String(c._id) : String(c)))
            .filter((id: string) => id && id !== 'undefined')
        } else if (key === 'location') {
          const loc = values.location as any
          if (loc == null) acc[key] = null
          else if (typeof loc === 'object' && (loc._id || loc.id)) {
            acc[key] = String(loc._id || loc.id)
          } else if (typeof loc === 'string') {
            acc[key] = loc
          }
        } else if (key === 'category') {
            acc[key] = categoryIdsForSubmit(values.category)
          } else if (key === 'types') {
            acc[key] = Array.isArray(values.types)
              ? values.types.map((cat: any) => (cat as any)._id)
              : values.types && (values.types as any)._id
                ? [(values.types as any)._id]
                : []
          } else if (key === 'gallery') {
            acc[key] = Array.isArray(values.gallery)
              ? values.gallery.map((img: any) => img._id || img)
              : []
          } else if (key === 'banner') {
            acc[key] = values.banner?._id || values.banner
          } else if (key === 'translations') {
          const allTranslations = tourAllLang?.translations ?? tour?.translations ?? []
          let originalTranslationId = ''
          const match = findTranslationByLanguage(allTranslations, selectedLanguage)
          if (match?._id) {
            originalTranslationId = String(match._id)
          }
          acc[key] = [
            buildTourTranslationPayload(
              values,
              selectedLanguage,
              allTranslations,
              originalTranslationId || undefined,
            ),
          ]
        } else if (key === 'bookingBlackoutRanges') {
          acc[key] = (values.bookingBlackoutRanges || [])
            .filter((r) => r?.from && r?.to)
            .map((r) => ({
              from: new Date(r.from),
              to: new Date(r.to),
              type: r.type === 'custom_request' ? 'custom_request' : 'blocked',
              message: r.message?.trim() || undefined,
            }))
        } else if (key === 'routeWaypoints') {
          acc[key] = (values.routeWaypoints || [])
            .map((w, i) => ({
              order: typeof w?.order === 'number' ? w.order : i + 1,
              title: (w?.title || '').trim() || undefined,
              lat: Number(w?.lat),
              lng: Number(w?.lng),
            }))
            .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
            .sort((a, b) => a.order - b.order)
        } else if (key === 'invalidBookingDateMessage') {
          acc[key] = values.invalidBookingDateMessage?.trim() || ''
        } else if (key === 'isBookable') {
          acc[key] = values.isBookable !== false
        } else if (key === 'notBookableMessage') {
          acc[key] = values.notBookableMessage || {}
        } else if (key === 'videos') {
          acc[key] = (values.videos || [])
            .filter((v: any) => v && String(v.url || '').trim())
            .map((v: any) => ({
              type: v.type || 'youtube',
              url: String(v.url).trim(),
              title: String(v.title || '').trim() || undefined,
            }))
        } else if (key === 'published') {
          acc.status = values.published ? 'published' : 'draft'
        } else {
          acc[key] = values[key as keyof TourDetails]
        }
        return acc
      }, {})

      // Remove empty values (keep bookingBlackoutRanges: [] to clear all blackouts)
      Object.keys(dataToSubmit).forEach(key => {
        if (key === 'bookingBlackoutRanges' && Array.isArray(dataToSubmit[key])) {
          return
        }
        if (key === 'routeWaypoints' && Array.isArray(dataToSubmit[key])) {
          return
        }
        if (key === 'routeCities' && Array.isArray(dataToSubmit[key])) {
          return
        }
        if (key === 'notBookableMessage' && typeof dataToSubmit[key] === 'object') {
          return
        }
        if (key === 'bookingDeadlineDays') {
          return
        }
        // Allow explicit city clear (null) and empty category array
        if (key === 'city' && dataToSubmit[key] === null) {
          return
        }
        if (key === 'category' && Array.isArray(dataToSubmit[key])) {
          return
        }
        // Allow empty videos[] so deletions persist on the API
        if (key === 'videos' && Array.isArray(dataToSubmit[key])) {
          return
        }
        // Allow clearing legacy YouTube field
        if (key === 'youtubeVideo' && typeof dataToSubmit[key] === 'string') {
          return
        }
        if (
          dataToSubmit[key] === null ||
          dataToSubmit[key] === undefined ||
          (typeof dataToSubmit[key] === 'string' && dataToSubmit[key].trim() === '') ||
          (Array.isArray(dataToSubmit[key]) && dataToSubmit[key].length === 0)
        ) {
          delete dataToSubmit[key]
        }
      })

      const shouldSyncLastBookingDate =
        modifiedFields.includes('bookingDeadlineDays') ||
        (modifiedFields.includes('endDate') &&
          isBookingDeadlineDaysProvided(values.bookingDeadlineDays))

      if (shouldSyncLastBookingDate) {
        if (isBookingDeadlineDaysProvided(values.bookingDeadlineDays)) {
          const computed = computeLastBookingDateFromDays(
            values.endDate,
            values.bookingDeadlineDays,
          )
          if (computed) {
            dataToSubmit.lastBookingDate = computed
          }
        } else if (modifiedFields.includes('bookingDeadlineDays') && values.endDate) {
          dataToSubmit.lastBookingDate = values.endDate
        }
      }

      // Keep unit in sync whenever duration is patched
      if (
        Object.prototype.hasOwnProperty.call(dataToSubmit, 'duration') ||
        Object.prototype.hasOwnProperty.call(dataToSubmit, 'durationUnit')
      ) {
        dataToSubmit.durationUnit = values.durationUnit === 'days' ? 'days' : 'hours'
        if (typeof dataToSubmit.duration === 'string') {
          dataToSubmit.duration = parseInt(dataToSubmit.duration, 10)
        }
      }

      return sanitizeTourPatchPayload(dataToSubmit)
    }
    
    // For POST requests, include all required fields
    const baseData: any = {
      category: categoryIdsForSubmit(values.category),
      youtubeVideo: values.youtubeVideo || '',
      videos: (values.videos || [])
        .filter((v: any) => v && String(v.url || '').trim())
        .map((v: any) => ({
          type: v.type || 'youtube',
          url: String(v.url).trim(),
          title: String(v.title || '').trim() || undefined,
        })),
      duration: typeof values.duration === 'string' 
        ? parseInt(values.duration, 10) 
        : values.duration,
      durationUnit: values.durationUnit === 'days' ? 'days' : 'hours',
      tourMinPeople: typeof values.tourMinPeople === 'string' 
        ? parseInt(values.tourMinPeople, 10) 
        : values.tourMinPeople,
      tourMaxPeople: typeof values.tourMaxPeople === 'string' 
        ? parseInt(values.tourMaxPeople, 10) 
        : values.tourMaxPeople,
      status: values.published ? 'published' : 'draft',
      city:
        typeof values.city === 'object' && values.city && (values.city as any)._id
          ? (values.city as any)._id
          : values.city || null,
      routeCities: Array.isArray(values.routeCities)
        ? values.routeCities
            .map((c: any) => (typeof c === 'object' && c?._id ? String(c._id) : String(c)))
            .filter((id: string) => id && id !== 'undefined')
        : [],
      location: null,
      faqs: values.faqs || [],
      banner: values.banner?._id,
      gallery: values.gallery?.map((img: images) => img._id) || [],
      content: values.translations[0]?.description || '',
      title: values.translations[0]?.title || '',
      translations: [
        buildTourTranslationPayload(values, selectedLanguage, tourAllLang?.translations),
      ],
      types: Array.isArray(values.types)
        ? values.types
            .map((type: any) => typeof type === 'string' ? type : (type && (type as any)._id ? (type as any)._id : null))
            .filter((id: any) => typeof id === 'string' && id)
        : values.types && (values.types as any)._id
          ? [(values.types as any)._id]
          : [],
    }
    
    // Add other fields for POST request
    if (values.startDate) {
      baseData.disponibilite_from = new Date(values.startDate)
    }
    
    if (values.endDate) {
      baseData.disponibilite_to = new Date(values.endDate)
    }
    
    if (values.endDate) {
      if (isBookingDeadlineDaysProvided(values.bookingDeadlineDays)) {
        const computed = computeLastBookingDateFromDays(
          values.endDate,
          values.bookingDeadlineDays,
        )
        if (computed) {
          baseData.lastBookingDate = new Date(computed)
        }
      } else {
        baseData.lastBookingDate = new Date(values.endDate)
      }
    }

    baseData.bookingBlackoutRanges = (values.bookingBlackoutRanges || [])
      .filter((r) => r?.from && r?.to)
      .map((r) => ({
        from: new Date(r.from),
        to: new Date(r.to),
        type: r.type === 'custom_request' ? 'custom_request' : 'blocked',
        message: r.message?.trim() || undefined,
      }))
    if (values.invalidBookingDateMessage?.trim()) {
      baseData.invalidBookingDateMessage = values.invalidBookingDateMessage.trim()
    }

    baseData.isBookable = values.isBookable !== false
    baseData.notBookableMessage = values.notBookableMessage || {}

    baseData.routeWaypoints = (values.routeWaypoints || [])
      .map((w, i) => ({
        order: typeof w?.order === 'number' ? w.order : i + 1,
        title: (w?.title || '').trim() || undefined,
        lat: Number(w?.lat),
        lng: Number(w?.lng),
      }))
      .filter((w) => Number.isFinite(w.lat) && Number.isFinite(w.lng))
      .sort((a, b) => a.order - b.order)

    if (values.departureTime?.trim()) {
      baseData.departureTime = values.departureTime.trim()
    }
    if (values.arrivalTime?.trim()) {
      baseData.arrivalTime = values.arrivalTime.trim()
    }

    baseData.cardBadgeColor =
      typeof values.cardBadgeColor === 'string' ? values.cardBadgeColor.trim() : ''

    return baseData
  }

  const tourValidationSchema = useMemo(() => {
    const bookingBlackoutRangesSchema = buildBookingBlackoutRangesSchema(intl)
    if (isNewTour) {
      return Yup.object().shape({
        translations: Yup.array().of(
          Yup.object().shape({
            title: Yup.string().required('Title is required'),
            description: Yup.string().required('Description is required'),
          }),
        ),
        tourMinPeople: Yup.number().min(1).required('Minimum number of people is required'),
        tourMaxPeople: Yup.number().min(1).required('Maximum number of people is required'),
        startDate: Yup.string().test('startDate', 'Start date must be before end date', function (value) {
          const { endDate } = this.parent
          if (!value || !endDate) return true
          return new Date(value) < new Date(endDate)
        }),
        endDate: Yup.string(),
        bookingDeadlineDays: Yup.number()
          .nullable()
          .transform((value, originalValue) =>
            originalValue === '' || originalValue === null || originalValue === undefined
              ? null
              : value,
          )
          .min(0, intl.formatMessage({ id: 'TOUR.AVAILABILITY.BOOKING_DEADLINE_DAYS_MIN' }))
          .test(
            'beforeEndDate',
            intl.formatMessage({ id: 'TOUR.AVAILABILITY.DATE_RULE_3' }),
            function (value) {
              const { endDate } = this.parent
              if (!isBookingDeadlineDaysProvided(value) || !endDate) return true
              const computed = computeLastBookingDateFromDays(endDate, value)
              if (!computed) return true
              return new Date(computed) <= new Date(endDate)
            },
          ),
        bookingBlackoutRanges: bookingBlackoutRangesSchema.optional(),
      })
    }
    return Yup.object().shape({
      bookingDeadlineDays: Yup.number()
        .nullable()
        .transform((value, originalValue) =>
          originalValue === '' || originalValue === null || originalValue === undefined
            ? null
            : value,
        )
        .min(0, intl.formatMessage({ id: 'TOUR.AVAILABILITY.BOOKING_DEADLINE_DAYS_MIN' }))
        .test(
          'beforeEndDate',
          intl.formatMessage({ id: 'TOUR.AVAILABILITY.DATE_RULE_3' }),
          function (value) {
            const { endDate } = this.parent
            if (!isBookingDeadlineDaysProvided(value) || !endDate) return true
            const computed = computeLastBookingDateFromDays(endDate, value)
            if (!computed) return true
            return new Date(computed) <= new Date(endDate)
          },
        ),
      bookingBlackoutRanges: bookingBlackoutRangesSchema.optional(),
    })
  }, [intl, isNewTour])

  // Render tab button with tooltip for disabled state
  const renderTabButton = (tab: typeof tabs[0]) => {
    const isDisabled = tab.requiresTourId && isNewTour
    const button = (
      <button
        key={tab.id}
        className={`tour-tab-item ${activeTab === tab.id ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
        onClick={() => handleTabClick(tab.id)}
        disabled={isDisabled}
        type="button"
      >
        {tab.name}
      </button>
    )

    if (isDisabled) {
      return (
        <OverlayTrigger
          key={tab.id}
          placement="top"
          overlay={
            <Tooltip id={`tooltip-${tab.id}`}>
              {intl.formatMessage({ id: 'TOUR.TABS.TOOLTIP_REQUIRES_GENERAL' })}
            </Tooltip>
          }
        >
          {button}
        </OverlayTrigger>
      )
    }
    return button
  }

  /** Persists map pin / location fields to `/locations` (same as former Location tab Save). */
  const persistTourLocationDocument = async (values: TourDetails) => {
    const loc = values.location
    if (!loc || !values.id) return
    const z = Number(loc.mapZoom)
    const locationData = {
      ...loc,
      mapLatitude: Number(loc.mapLatitude),
      mapLongitude: Number(loc.mapLongitude),
      mapZoom: Number.isFinite(z) ? z : 7,
      title: loc.title || '',
      realAddress: loc.realAddress || '',
    }
    if (!loc._id) {
      const res = (await createOne({ ...locationData, tour: values.id }, '/locations')) as {
        data?: { location?: { _id: string } }
      }
      if (res?.data?.location?._id) {
        await patchOne({ location: res.data.location._id }, `/tours/${values.id}`)
      }
    } else {
      await patchOne(locationData, `/locations/${loc._id}`)
    }
  }

  const formik = useFormik({
    initialValues: tour || initialTour,
    validationSchema: tourValidationSchema,
    enableReinitialize: true,
    validateOnChange: isNewTour,
    validateOnBlur: isNewTour,
    validateOnMount: isNewTour,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        setSubmitting(true)
        setIsSubmitting(true)

        if (isNewTour) {
          const dataToSubmit = prepareDataForSubmit(values)
          // For new tours, validate and submit
          try {
            await tourValidationSchema.validate(values, { abortEarly: false })
            const response = await createOne(dataToSubmit, "/tours")
            if (response?.data?.tour?._id) {
              const newTourId = response.data.tour._id
              toast.success(intl.formatMessage(
                { id: 'TOUR.ADDED_SUCCESSFULLY' }, 
                { defaultMessage: 'Tour added successfully' }
              ))
              queryClient.invalidateQueries(['tours-list'])
              navigate(`/apps/tours/${newTourId}`, { 
                replace: true,
                state: { initialTab: 'location' }
              })
            }
          } catch (validationError: any) {
            if (validationError.inner) {
              const errors: any = {}
              validationError.inner.forEach((err: any) => {
                errors[err.path] = err.message
              })
              setErrors(errors)
            }
            setIsSubmitting(false)
            setSubmitting(false)
            return
          }
        } else {
          // For existing tours, submit without validation
          try {
            if (activeTabRef.current === 'location') {
              await persistTourLocationDocument(values)
            }
            const dataToSubmit = prepareDataForSubmit(values)
            if (!dataToSubmit || Object.keys(dataToSubmit).length === 0) {
              toast.info(
                intl.formatMessage({
                  id: 'ADMIN.COMMON.ERROR.NO_MODIFICATION_DETECTED',
                  defaultMessage: 'No changes detected.',
                }),
              )
              setIsSubmitting(false)
              setSubmitting(false)
              return
            }
            const response = await patchOne(dataToSubmit, `/tours/${id}`)
            const ok =
              response?.data != null ||
              response?.payload?.code === 2001 ||
              response?.payload?.code === 2021
            if (ok) {
              queryClient.invalidateQueries(['tours-list'])
              queryClient.invalidateQueries([`tour-${id}-${selectedLanguage}`])
              queryClient.invalidateQueries([`tour-all-lang-${id}`])
              queryClient.invalidateQueries([`tour-content-categories-${id}`])
              await queryClient.refetchQueries([`tour-${id}-${selectedLanguage}`])
              await queryClient.refetchQueries([`tour-all-lang-${id}`])
              await queryClient.refetchQueries([`tour-content-categories-${id}`])
              toast.success(intl.formatMessage(
                { id: 'TOUR.UPDATED_SUCCESSFULLY' }, 
                { defaultMessage: 'Tour updated successfully' }
              ))
              refetchData()
            } else {
              toast.error(
                response?.payload?.message ||
                  intl.formatMessage({
                    id: 'TOUR.UPDATE_ERROR',
                    defaultMessage: 'Tour update failed',
                  }),
              )
            }
          } catch (apiError: any) {
            throw apiError
          }
        }
      } catch (error: any) {
        toast.error(
          error?.response?.data?.payload?.message ||
            error?.message ||
            'An error occurred while saving the tour',
        )
      } finally {
        setIsSubmitting(false)
        setSubmitting(false)
      }
    },
  })

  // Direct form submission handler
  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (
      activeTab !== 'general' &&
      activeTab !== 'availability' &&
      !(activeTab === 'location' && !isNewTour)
    ) {
      return
    }
    
    // For existing tours, clear any validation errors and submit without validation
    if (!isNewTour) {
      // Clear any existing validation errors
      formik.setErrors({})
      formik.handleSubmit();
      return;
    }
    
    // For new tours, show validation errors
    if (Object.keys(formik.errors).length > 0) {
      // Set all fields as touched to show errors
      const touchedFields: any = {};
      Object.keys(formik.errors).forEach(field => {
        touchedFields[field] = true;
        
        // Handle nested fields like translations
        if (field === 'translations' && formik.errors.translations) {
          touchedFields.translations = [{ title: true, description: true }];
        }
      });
      
      formik.setTouched(touchedFields);
      
      // Show alert with error message
      const errorMessages = Object.entries(formik.errors)
        .map(([field, error]) => {
          // Handle special cases for better user messages
          if (field === 'youtubeVideo') {
            return 'YouTube video URL is required';
          } else if (field === 'category') {
            return 'Category is required';
          } else if (field === 'city') {
            return 'City is required';
          } else if (field === 'translations') {
            return 'Title and description are required';
          } else if (field === 'tourMinPeople') {
            return 'Tour minimum people is required';
          } else if (field === 'tourMaxPeople') {
            return 'Tour maximum people is required';
          } else if (field === 'startDate') {
            return 'Start date is required';
          } else if (field === 'endDate') {
            return 'End date is required';
          } else if (field === 'bookingDeadlineDays') {
            return 'Booking deadline (days) is invalid';
          }
          return `${field} is required`;
        })
        .join(', ');
      
      // Show error toast
      toast.error(`Please fill all required fields: ${errorMessages}`);
    }
    
    formik.handleSubmit();
  }

  // Handle delete button click
  const deleteConfirmExpected = (
    formik.values?.translations?.[0]?.title ||
    (tour as any)?.translations?.[0]?.title ||
    (tour as any)?.title ||
    ''
  ).trim()
  const deleteConfirmMatches =
    !!deleteConfirmExpected && deleteConfirmText.trim() === deleteConfirmExpected

  const handleDeleteClick = () => {
    if (!canDeleteTour) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_SUPERADMIN_ONLY',
          defaultMessage: 'Seuls les Super Admins peuvent supprimer un tour.',
        }),
      )
      return
    }
    setDeleteConfirmText('')
    setShowDeleteConfirm(true)
  }
  
  // Confirm deletion
  const confirmDelete = async () => {
    if (!id) {
      toast.error('Tour ID is missing')
      return
    }
    if (!canDeleteTour) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_SUPERADMIN_ONLY',
          defaultMessage: 'Seuls les Super Admins peuvent supprimer un tour.',
        }),
      )
      return
    }
    if (deleteConfirmText.trim() !== deleteConfirmExpected) {
      toast.error(
        intl.formatMessage({
          id: 'TOUR.DELETE_CONFIRM_MISMATCH',
          defaultMessage: 'Le nom du tour saisi ne correspond pas.',
        }),
      )
      return
    }
    
    try {
      setIsDeleting(true)
      
      // Call the delete API with proper type casting
      await deleteOne(String(id) as unknown as ID, '/tours')
      
      // Close the modal
      setShowDeleteConfirm(false)
      setDeleteConfirmText('')
      
      // Show success message before redirecting
      toast.success(intl.formatMessage({
        id: 'TOUR.DELETED_SUCCESSFULLY',
        defaultMessage: 'Tour has been deleted successfully'
      }))
      
      // Set delete successful flag
      setDeleteSuccessful(true)
      
      // Add a small delay before redirecting to allow toast to be seen
      setTimeout(() => {
        navigate('/apps/tours')
      }, 1000)
    } catch (error) {
      // Show error message
      toast.error(intl.formatMessage({
        id: 'TOUR.DELETE_ERROR',
        defaultMessage: 'Failed to delete tour. Please try again.'
      }))
      setIsDeleting(false)
    }
  }

  useEffect(() => {
    // Only load existing tour data if we're on the edit page
    const isAddPage = window.location.pathname.includes('/new') || id === 'new';
    
    // If delete was successful, don't load tour data
    if (deleteSuccessful) {
      return;
    }
    
    if (tour && !isAddPage) {
      const publishedStatus = tour.status === 'published';
      const cityValue = toRefId(tour.city);

      const translationForLang =
        findTranslationByLanguage(tourAllLang?.translations, selectedLanguage) ||
        findTranslationByLanguage(tour.translations, selectedLanguage)
      const emptyTranslation = {
        language: selectedLanguage,
        title: '',
        description: '',
        shortDescription: '',
        longDescription: '',
    note: '',
    cardBadge: '',
    seoSlug: '',
        metaTitle: '',
        metaDescription: '',
        metaKeywords: [] as string[],
        openGraphTitle: '',
        openGraphDescription: '',
        openGraphImage: '',
        openGraphType: '',
        openGraphLocale: selectedLanguage,
        twitterTitle: '',
        twitterDescription: '',
        twitterImage: '',
        twitterCardType: '',
        metaRobots: '',
        canonicalUrl: '',
        structuredData: '',
      }
      const translationBlock = translationForLang
        ? { ...emptyTranslation, ...translationForLang, language: selectedLanguage }
        : emptyTranslation

      const routeCityIds = Array.isArray((tour as any).routeCities)
        ? (tour as any).routeCities.map((c: any) =>
            typeof c === 'object' && c?._id ? String(c._id) : String(c),
          )
        : []

      const contentCategories = contentCategoriesFetched
        ? contentCategoriesAll || []
        : Array.isArray(formik.values.contentCategories) &&
            formik.values.contentCategories.length > 0
          ? formik.values.contentCategories
          : []
      // While by-tour?language=all is loading, do not fall back to tour.contentCategories
      // (that race caused includes/excludes to flip between base titles and translations).
      if (!contentCategoriesFetched && !contentCategories.length) {
        return
      }
      const includesSource = (contentCategories as any[])?.find((item) =>
        isContentCategoryType(item?.type, 'includes'),
      )
      const excludesSource = (contentCategories as any[])?.find((item) =>
        isContentCategoryType(item?.type, 'excludes'),
      )

      const formValues = {
        ...tour,
        contentCategories,
        // Keep display fields in sync — setValues would otherwise wipe them
        includes: localizeDetails(includesSource?.details, selectedLanguage),
        excludes: localizeDetails(excludesSource?.details, selectedLanguage),
        durationUnit: (tour as any).durationUnit === 'days' ? 'days' : 'hours',
        city: cityValue, // Always use the string ID, never the object
        routeCities: routeCityIds,
        published: publishedStatus,
        startDate: tour.disponibilite_from,
        endDate: tour.disponibilite_to,
        bookingDeadlineDays: computeBookingDeadlineDays(
          tour.disponibilite_to,
          tour.lastBookingDate,
        ),
        bookingBlackoutRanges: (tour as any).bookingBlackoutRanges?.length
          ? (tour as any).bookingBlackoutRanges.map((r: any) => ({
              from: r.from ? String(r.from).split('T')[0] : '',
              to: r.to ? String(r.to).split('T')[0] : '',
              type: r.type === 'custom_request' ? 'custom_request' : 'blocked',
              message: r.message || '',
            }))
          : [],
        invalidBookingDateMessage: (tour as any).invalidBookingDateMessage || '',
        cardBadgeColor: (tour as any).cardBadgeColor || '',
        price: tour.price?.priceTTC ?? tour.price ?? '',
        promoPrice: tour.price?.priceHT ?? '',
        location: tour.location || null, // Ensure location is properly set
        seoSlug: (tour as any).seoSlug || (tourAllLang as any)?.seoSlug || formik.values.seoSlug || '',
        seoTitle: (tour as any).seoTitle || (tourAllLang as any)?.seoTitle || formik.values.seoTitle || '',
        seoDescription:
          (tour as any).seoDescription ||
          (tourAllLang as any)?.seoDescription ||
          formik.values.seoDescription ||
          '',
        translations: [translationBlock],
        routeWaypoints: Array.isArray((tour as any).routeWaypoints)
          ? (tour as any).routeWaypoints.map((w: any, i: number) => ({
              order: typeof w?.order === 'number' ? w.order : i + 1,
              title: w?.title ?? '',
              lat: Number(w?.lat) || 0,
              lng: Number(w?.lng) || 0,
            }))
          : [],
      };
      
      formik.setValues(formValues);
    } else if (isAddPage) {
      // Ensure we're using initialTour for new tours
      formik.resetForm({
        values: {
          ...initialTour,
          location: null
        }
      });
    }
  }, [tour, tourAllLang, contentCategoriesAll, contentCategoriesFetched, id, deleteSuccessful, selectedLanguage]);

  const pageTitle = isNewTour 
    ? intl.formatMessage({ id: 'TOUR.ADD' }) 
    : intl.formatMessage({ id: 'TOUR.DETAILS' })

  const renderTabContent = () => {
    switch (activeTab) {
      case 'general':
        return formik ? (
          <GeneralTab 
            formik={formik} 
            intl={intl} 
            selectedLanguage={selectedLanguage} 
            setSelectedLanguage={setSelectedLanguage} 
            isNewTour={isNewTour} 
          />
        ) : null
      case 'location':
        return formik ? <LocationTab formik={formik} intl={intl} isActive={activeTab === 'location'} selectedLanguage={selectedLanguage} setSelectedLanguage={setSelectedLanguage} /> : null
      case 'pricing':
        // Pricing tab now handles its own saving and doesn't rely on the parent's submit
        return formik ? <PricingTab formik={formik} intl={intl} selectedLanguage={selectedLanguage} /> : null
      case 'faq-includes-excludes':
        return formik && id ? (
          <FaqIncludesExcludesTab 
            formik={formik} 
            intl={intl} 
            tourId={id} 
            hideFAQ={true}
            fetchTourDetails={fetchTourDetails}
            selectedLanguage={selectedLanguage}
            setSelectedLanguage={setSelectedLanguage}
          />
        ) : null
      case 'availability':
        return formik ? (
          <AvailabilityTab
            formik={formik}
            intl={intl}
            selectedLanguage={selectedLanguage}
          />
        ) : null
      case 'seo':
        return formik ? (
          <SeoTab
            formik={formik}
            intl={intl}
            selectedLanguage={selectedLanguage}
            tourId={isNewTour ? undefined : id}
            allTranslations={tourAllLang?.translations ?? tour?.translations}
            onSaved={async () => {
              if (!id || isNewTour) return
              queryClient.invalidateQueries([`tour-${id}-${selectedLanguage}`])
              queryClient.invalidateQueries([`tour-all-lang-${id}`])
              await queryClient.refetchQueries([`tour-${id}-${selectedLanguage}`])
              await queryClient.refetchQueries([`tour-all-lang-${id}`])
            }}
          />
        ) : null
      default:
        return null
    }
  }

  return (
    <>
      <AzPageHeader title={pageTitle} />
      
      {/* Validation Error Alert */}
      {isNewTour && Object.keys(formik.errors).length > 0 && formik.submitCount > 0 && (
        <div className="alert alert-danger mb-5">
          <div className="d-flex">
            <i className="fas fa-exclamation-triangle fs-2x mr-2"></i>
            <div>
              <h4 className="mb-1">Form Validation Error</h4>
              <div>Please fill in all required fields:</div>
              <ul className="mt-2 mb-0">
                {Object.entries(formik.errors).map(([field, error], index) => {
                  let errorMessage = '';
                  
                  // Handle special cases for better user messages
                  if (field === 'youtubeVideo') {
                    errorMessage = 'YouTube video URL is required';
                  } else if (field === 'category') {
                    errorMessage = 'Category is required';
                  } else if (field === 'city') {
                    errorMessage = 'City is required';
                  } else if (field === 'translations') {
                    errorMessage = 'Title and description are required';
                  } else if (field === 'tourMinPeople') {
                    errorMessage = 'Tour minimum people is required';
                  } else if (field === 'tourMaxPeople') {
                    errorMessage = 'Tour maximum people is required';
                  } else if (field === 'startDate') {
                    errorMessage = 'Start date is required';
                  } else if (field === 'endDate') {
                    errorMessage = 'End date is required';
                  } else if (field === 'bookingDeadlineDays') {
                    errorMessage = 'Booking deadline (days) is required';
                  }
                  return <li key={index}>{errorMessage}</li>
                })}
              </ul>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          role='dialog'
          aria-modal='true'
          style={{backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1055}}
        >
          <div className='modal-dialog modal-dialog-centered'>
            <div
              className='modal-content border-0 shadow-lg'
              style={{borderRadius: 16, overflow: 'hidden'}}
            >
              <div
                className='px-6 pt-6 pb-4'
                style={{
                  background: 'linear-gradient(135deg, #7f1d1d 0%, #b91c1c 55%, #dc2626 100%)',
                  color: '#fff',
                }}
              >
                <div className='d-flex align-items-start justify-content-between gap-3'>
                  <div className='d-flex align-items-start gap-3'>
                    <span
                      className='d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0'
                      style={{
                        width: 44,
                        height: 44,
                        backgroundColor: 'rgba(255,255,255,0.18)',
                      }}
                    >
                      <span className='material-symbols-outlined' style={{fontSize: 24}}>
                        warning
                      </span>
                    </span>
                    <div>
                      <h5 className='mb-1 fw-bold text-white'>
                        {intl.formatMessage({
                          id: 'TOUR.DELETE_IRREVERSIBLE_TITLE',
                          defaultMessage: 'Suppression définitive',
                        })}
                      </h5>
                      <div className='fs-7 text-white' style={{opacity: 0.9}}>
                        {intl.formatMessage({
                          id: 'TOUR.DELETE_IRREVERSIBLE_WARNING',
                          defaultMessage:
                            'Cette action est irréversible. Le tour et ses données associées seront définitivement supprimés.',
                        })}
                      </div>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='btn btn-sm btn-icon'
                    onClick={() => {
                      setShowDeleteConfirm(false)
                      setDeleteConfirmText('')
                    }}
                    disabled={isDeleting}
                    style={{color: '#fff', opacity: 0.9}}
                    aria-label='Close'
                  >
                    <span className='material-symbols-outlined'>close</span>
                  </button>
                </div>
              </div>

              <div className='px-6 py-5'>
                <div
                  className='mb-4 px-4 py-3 rounded-3'
                  style={{
                    backgroundColor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <div className='text-uppercase fs-8 fw-bold text-slate-500 mb-1'>
                    {intl.formatMessage({
                      id: 'TOUR.DELETE_TOUR_LABEL',
                      defaultMessage: 'Tour à supprimer',
                    })}
                  </div>
                  <div className='fw-bold text-gray-900 fs-5'>
                    {deleteConfirmExpected || '—'}
                  </div>
                </div>

                <label className='form-label fw-semibold text-gray-800 mb-2'>
                  {intl.formatMessage(
                    {
                      id: 'TOUR.DELETE_TYPE_TOUR_NAME',
                      defaultMessage: 'Pour confirmer, saisissez le nom du tour « {name} » :',
                    },
                    {name: deleteConfirmExpected || '…'},
                  )}
                </label>
                <input
                  type='text'
                  className='form-control form-control-lg'
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  disabled={isDeleting || !deleteConfirmExpected}
                  placeholder={deleteConfirmExpected}
                  autoFocus
                  style={{
                    borderColor:
                      deleteConfirmText && !deleteConfirmMatches ? '#dc2626' : '#cbd5e1',
                    backgroundColor: '#fff',
                  }}
                />
              </div>

              <div
                className='px-6 py-4 d-flex justify-content-end gap-2'
                style={{backgroundColor: '#f8fafc', borderTop: '1px solid #e2e8f0'}}
              >
                <button
                  type='button'
                  className='btn btn-light'
                  onClick={() => {
                    setShowDeleteConfirm(false)
                    setDeleteConfirmText('')
                  }}
                  disabled={isDeleting}
                >
                  {intl.formatMessage({id: 'ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className='btn fw-semibold'
                  onClick={confirmDelete}
                  disabled={isDeleting || !deleteConfirmMatches}
                  style={{
                    backgroundColor: deleteConfirmMatches ? '#b91c1c' : '#fecaca',
                    borderColor: deleteConfirmMatches ? '#b91c1c' : '#fecaca',
                    color: deleteConfirmMatches ? '#fff' : '#7f1d1d',
                    opacity: deleteConfirmMatches ? 1 : 0.7,
                  }}
                >
                  {isDeleting ? (
                    <>
                      <span
                        className='spinner-border spinner-border-sm'
                        role='status'
                        aria-hidden='true'
                      ></span>
                      <span className='ms-2'>
                        {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}
                      </span>
                    </>
                  ) : (
                    intl.formatMessage({id: 'ACTION.DELETE'})
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <KTCard>
        <div className="px-4 pt-4">
          <StickyLanguageSelector
            selectedLanguage={selectedLanguage}
            onLanguageChange={setSelectedLanguage}
          />
        </div>
        <div className="tour-tabs-container ms-0 ms-md-8 mt-4 px-4 px-md-0">
          <h2 className="tour-title">
            {isNewTour ? 'Add New Tour' : 'Tour'}
          </h2>
          <div className="tour-tabs">
            {tabs.map(tab => renderTabButton(tab))}
          </div>
        </div>
        <KTCardBody className='py-4'>
          <div className='py-4 app-content flex-column-fluid'>
            <div className='d-flex flex-column gap-7 gap-lg-10'>
              <form 
                onSubmit={handleManualSubmit}
                data-method={isNewTour ? 'POST' : 'PATCH'}
                data-tour-id={id}
                noValidate={!isNewTour}
              >
                <div className='tab-content' id='myTabContent'>
                  {renderTabContent()}
                </div>

                {(activeTab === 'general' ||
                  activeTab === 'availability' ||
                  (activeTab === 'location' && !isNewTour)) && (
                  <div className='d-flex justify-content-end mt-5'>
                    <button 
                      type='button' 
                      className='btn btn-secondary me-2' 
                      onClick={handleCancel}
                      disabled={isSubmitting || isDeleting}
                    >
                      {intl.formatMessage({ id: 'ACTION.CANCEL' })}
                    </button>
                    <button 
                      type='submit' 
                      className='btn btn-primary'
                      disabled={isSubmitting || isDeleting}
                    >
                      {isSubmitting ? (
                        <>
                          <span className='spinner-border spinner-border-sm' role='status' aria-hidden='true'></span>
                          <span className='ms-2'>{intl.formatMessage({ id: 'COMMON.PLEASE_WAIT' })}</span>
                        </>
                      ) : (
                        intl.formatMessage({ id: 'TOUR.ACTIONS.SAVE' })
                      )}
                    </button>
                  </div>
                )}
              </form>
            </div>
          </div>
        </KTCardBody>
      </KTCard>
    </>
  )
}

export { TourDetailsPage } 